import React from 'react';
import PropTypes from 'prop-types';

import ALink from 'tradera-components/alink/alink';
import MultiCurrency, {
    preferredCurrencyPropTypes
} from '../multi-currency/multi-currency';

import EndTimeLabel from './item-card-parts/end-time-label';
import AttributeButtonsList from './item-card-parts/attribute-buttons-list';
import BidsLabel from './item-card-parts/bids-label';
import BuyNowLabel from './item-card-parts/buy-now-label';
import DiscountBadge from './item-card-parts/discount-badge';
import DiscountedPriceLabel from './item-card-parts/disacount-price-label';
import FixedPriceLabel from './item-card-parts/fixed-price-label';
import ItemCardImage from './item-card-parts/item-card-image';
import NewTodayLabel from './item-card-parts/new-today-label';
import OnDisplayBadge from './item-card-parts/on-display-badge';
import PriceLabel from './item-card-parts/price-label';
import QuickViewButton from './item-card-parts/quick-view-button';
import SellerLink from './item-card-parts/seller-link';
import SettingsButton from './item-card-parts/settings-button';
import ShippingLabel from './item-card-parts/shipping-label';
import WishListButton from './item-card-parts/wish-list-button';
import './item-card.scss';
import { useSelector } from 'react-redux';
import { selectIsSwedishVisitor } from 'tradera-state/shipping-region/selectors';
import ITEM_TYPES from 'tradera-constants/item-types';
import dayjs from 'dayjs';

function ItemListCard({
    className,
    item = {},
    lazy,
    onClick,
    onAddToWishlist,
    onQuickView,
    onRemoveFromWishlist,
    onAttributeButtonClick,
    onSettings,
    preferredCurrency,
    ready,
    isShopOwnedByCurrentMember = false
}) {
    const inSPA = true;
    const existsInWishList = useSelector(state =>
        state.items.wishListItemIds.has(item.itemId)
    );
    const isSwedishVisitor = useSelector(selectIsSwedishVisitor);
    ///////
    const showBids = [ITEM_TYPES.AUCTION, ITEM_TYPES.AUCTION_BIN].includes(
        item.itemType
    );
    const showBuyNow =
        item.itemType === ITEM_TYPES.AUCTION_BIN &&
        (item.totalBids === 0 || !item.reservePriceReached);
    const isShopItem = item.itemType === ITEM_TYPES.SHOP_ITEM;
    const hasDiscount = item.discountRate > 0;

    const isNewToday =
        !!item.startDate &&
        dayjs(item.startDate).isAfter(dayjs().subtract(1, 'day'));
    const itemUrl = item.itemUrl || '/';
    const imageUrl = item.imageUrl?.replace('/medium/', '/medium-fit/');
    ///////

    const handleWishlist = event => {
        event.preventDefault();
        event.stopPropagation();
        if (existsInWishList) {
            onRemoveFromWishlist(item.itemId);
        } else {
            onAddToWishlist(item.itemId);
        }
    };

    return (
        <div
            id={`item-card-${item.itemId}`}
            data-item-loaded={ready ? 'true' : 'false'}
            className={`item-card-new item-card-list flex-row
                ${ready ? '' : 'item-card-loading'}
                ${item.displayHighlighted ? 'item-card-new-highlight' : ''}
                ${className}`}>
            <ItemCardImage
                className="flex-grow-0 flex-shrink-0"
                lazy={lazy}
                itemUrl={itemUrl}
                imageUrl={imageUrl}
                inSPA={inSPA}
                shortDescription={item.shortDescription}
                onClick={onClick}
            />

            <div className="mx-1 mx-md-2 py-md-1 d-flex flex-column flex-grow-1 overflow-hidden">
                <div className="d-none d-md-flex d-row align-items-start">
                    {hasDiscount && (
                        <DiscountBadge
                            className="mr-2"
                            discountRate={item.discountRate}
                        />
                    )}
                    <h4 className="item-card-title mb-0">
                        <ALink
                            href={itemUrl}
                            id={`item_${item.itemId}`}
                            onClick={onClick}
                            title={item.shortDescription}
                            useLink={inSPA}>
                            {item.shortDescription}
                        </ALink>
                    </h4>
                </div>
                <div className="d-flex d-md-none d-row align-items-start">
                    <h4 className="heading-paris">
                        <ALink
                            href={itemUrl}
                            id={`item_${item.itemId}`}
                            useLink={inSPA}>
                            {item.shortDescription}
                        </ALink>
                    </h4>
                </div>
                {!!item.attributes?.length && onAttributeButtonClick && (
                    <AttributeButtonsList
                        className="mt-1"
                        attributes={item.attributes}
                        onClick={onAttributeButtonClick}
                    />
                )}
                <div className="mb-1 d-none d-md-flex flex-row mt-auto mr-auto">
                    <SellerLink
                        className="item-card-list-detail-spaced flex-grow-1 text-truncate mr-1"
                        sellerMemberId={item.sellerMemberId}
                        sellerAlias={item.sellerAlias}
                        sellerDsrAverage={item.sellerDsrAverage}
                    />
                    {isShopItem ? (
                        <FixedPriceLabel />
                    ) : (
                        <>
                            {isNewToday && <NewTodayLabel className="mr-1" />}
                            <EndTimeLabel
                                endDate={item.endDate}
                                isActive={item.isActive}
                            />
                        </>
                    )}
                </div>

                <div className="item-card-buttons d-flex flex-row flex-grow-0 flex-shrink-0 align-items-center mt-auto">
                    <WishListButton
                        className="btn-outline-dark d-md-none"
                        onClick={handleWishlist}
                        showText={false}
                        toggled={existsInWishList}
                    />
                    <WishListButton
                        className="btn-outline-dark d-none d-md-block"
                        onClick={handleWishlist}
                        showText={true}
                        toggled={existsInWishList}
                    />
                    {onQuickView && (
                        <QuickViewButton
                            className="btn-outline-dark ml-1 d-none d-lg-inline-block"
                            onClick={() => onQuickView(item.itemId)}
                        />
                    )}
                    {isShopOwnedByCurrentMember && (
                        <SettingsButton
                            className="btn-outline-dark ml-1 d-none d-sm-block"
                            onClick={() => onSettings(item.itemId)}
                        />
                    )}
                </div>
            </div>

            <div
                className={`item-card-details mx-1 mx-md-2 pl-1 py-md-1 text-right d-flex flex-column flex-grow-0 align-items-end`}>
                <PriceLabel
                    tagName="h4"
                    className="text-nowrap mb-0 d-none d-md-block text-styled"
                    price={item.price}
                    hasDiscount={hasDiscount}
                />
                <PriceLabel
                    tagName="p"
                    className="text-nowrap mb-0 d-block d-md-none"
                    price={item.price}
                    hasDiscount={hasDiscount}
                />
                <MultiCurrency
                    className="mb-0 ml-2"
                    amountInSek={item.price}
                    preferredCurrency={preferredCurrency}
                    tagName="p"
                />
                {hasDiscount && (
                    <DiscountedPriceLabel
                        className="mb-0"
                        price={item.priceBeforeDiscount}
                        tagName="p"
                    />
                )}
                {showBids && (
                    <BidsLabel
                        className="mb-0"
                        tagName="p"
                        count={item.totalBids}
                    />
                )}
                {showBuyNow && (
                    <>
                        <BuyNowLabel className="mb-0" tagName="p" />
                        <PriceLabel
                            className="text-nowrap font-weight-bold d-none d-md-inline-block"
                            price={item.buyNowPrice}
                            tagName="p"
                        />
                    </>
                )}
                {isSwedishVisitor && (
                    <ShippingLabel
                        className="mb-0"
                        hasFreeShipping={item.hasFreeShipping}
                        shippingOptions={item.shippingOptions}
                        tagName="p"
                    />
                )}
                {item.onDisplay && <OnDisplayBadge />}
                <p className="text-truncate mt-auto mb-0 ml-auto d-block d-md-none">
                    {isShopItem ? (
                        <FixedPriceLabel />
                    ) : (
                        <>
                            {isNewToday && <NewTodayLabel className="mr-2" />}
                            <EndTimeLabel
                                endDate={item.endDate}
                                isActive={item.isActive}
                            />
                        </>
                    )}
                </p>
            </div>
        </div>
    );
}

ItemListCard.defaultProps = {
    className: '',
    onAddToWishlist: () => {},
    onRemoveFromWishlist: () => {},
    onQuickView: () => {}
};

ItemListCard.propTypes = {
    className: PropTypes.string,
    item: PropTypes.shape({
        attributes: PropTypes.array,
        buyNowPrice: PropTypes.number,
        discountRate: PropTypes.number,
        displayHighlighted: PropTypes.bool,
        endDate: PropTypes.string,
        hasFreeShipping: PropTypes.bool,
        imageHeight: PropTypes.number,
        imageUrl: PropTypes.string,
        imageWidth: PropTypes.number,
        isActive: PropTypes.bool,
        itemId: PropTypes.number,
        itemType: PropTypes.string,
        itemUrl: PropTypes.string,
        onDisplay: PropTypes.bool,
        price: PropTypes.number,
        priceBeforeDiscount: PropTypes.number,
        reservePriceReached: PropTypes.bool,
        sellerAlias: PropTypes.string,
        sellerDsrAverage: PropTypes.number,
        sellerMemberId: PropTypes.number,
        shippingOptions: PropTypes.array,
        shortDescription: PropTypes.string,
        startDate: PropTypes.string,
        totalBids: PropTypes.number,

        /* Currently only used in Wish list */
        isOverbidden: PropTypes.bool,
        isLeading: PropTypes.bool,
        isWinner: PropTypes.bool
    }),
    lazy: PropTypes.shape({
        offset: PropTypes.number,
        once: PropTypes.bool,
        height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        placeholder: PropTypes.node,
        imagePlaceholder: PropTypes.node
    }),
    onAddToWishlist: PropTypes.func,
    onRemoveFromWishlist: PropTypes.func,
    onQuickView: PropTypes.func,
    onSettings: PropTypes.func,
    preferredCurrency: preferredCurrencyPropTypes,
    ready: PropTypes.bool,
    isShopOwnedByCurrentMember: PropTypes.bool
};

export default ItemListCard;
